"use client";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useMonthlyUsageStats } from "@/hooks/use-monthly-usage-stats";
import { UsageEmpty } from "@/components/usage-empty";
import type { MonthlyUsageStats } from "@/services/statistics.service";
import { calculateUsageChange, type UsageChange } from "@/lib/usage-comparison";
import { UsageStatCard } from "@/components/usage-stat-card";
import { UsageUnavailable } from "@/components/usage-unavailable";

interface StatItem extends UsageChange {
  title: string;
  value: string;
  description: string;
}

const formatNumber = (num: number | undefined): string => {
  return (num ?? 0).toLocaleString();
};

const transformStatsToDisplay = (
  stats: MonthlyUsageStats,
  previousStats?: MonthlyUsageStats
): StatItem[] => {
  const prev =
    previousStats ||
    ({
      twoDTo3D: 0,
      twoDTo3DUsers: 0,
    } as MonthlyUsageStats);

  const avgModelsPerUser = stats.twoDTo3DUsers > 0
    ? stats.twoDTo3D / stats.twoDTo3DUsers
    : 0;

  const prevAvgModelsPerUser = prev.twoDTo3DUsers > 0
    ? prev.twoDTo3D / prev.twoDTo3DUsers
    : 0;

  return [
    {
      title: "3D Models Created",
      value: formatNumber(stats.twoDTo3D),
      description: "Models generated this month",
      ...calculateUsageChange(stats.twoDTo3D, prev.twoDTo3D),
    },
    {
      title: "Unique Users",
      value: formatNumber(stats.twoDTo3DUsers),
      description: "Users who used 2D to 3D",
      ...calculateUsageChange(stats.twoDTo3DUsers, prev.twoDTo3DUsers),
    },
    {
      title: "Avg. Models per User",
      value: avgModelsPerUser.toFixed(1),
      description: "Average models per user",
      ...calculateUsageChange(avgModelsPerUser, prevAvgModelsPerUser),
    },
  ];
};

export function TwoDToThreeDStats() {
  const { currentStats, previousStats, error, previousError, isLoading, previousLoading } = useMonthlyUsageStats();

  if (error) return <UsageUnavailable />;

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3].map((i) => (
          <Card
            key={i}
            className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-purple-200 animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-purple-200 animate-pulse rounded mb-2" />
              <div className="h-3 w-32 bg-purple-200 animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!currentStats) return <UsageEmpty />;

  const stats = transformStatsToDisplay(currentStats, previousStats ?? undefined);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat) => (
        <UsageStatCard key={stat.title} title={stat.title} value={stat.value}
          description={stat.description} comparison={previousError ? calculateUsageChange(undefined) : stat}
          period="previous month" comparisonLoading={previousLoading} />
      ))}
    </div>
  );
}
